import { useState } from 'react';
import Modal from '@/components/base/Modal';
import type { ApiKey } from '@/mocks/integrations';

interface RevokeKeyModalProps {
  open: boolean;
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: (id: string) => void;
}

export default function RevokeKeyModal({ open, apiKey, onClose, onConfirm }: RevokeKeyModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!apiKey) return;
    if (confirmText.trim() !== apiKey.platform) {
      setError(`Please type "${apiKey.platform}" exactly to confirm revocation.`);
      return;
    }
    onConfirm(apiKey.id);
    setConfirmText('');
    setError('');
    onClose();
  };

  const inputClasses =
    'w-full bg-background-100/70 border border-background-200/90 rounded-xl px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-rose-500 focus:bg-background-50 focus:ring-2 focus:ring-rose-500/15 shadow-2xs transition-all';

  return (
    <Modal
      open={open}
      title="Revoke API Key"
      subtitle="This action is permanent and will immediately cut off API access for this integration."
      onClose={onClose}
    >
      {apiKey && (
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl bg-rose-50 border border-rose-200/80 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-xl bg-rose-500 text-white flex items-center justify-center shrink-0 shadow-2xs">
                <i className="ri-error-warning-line text-base"></i>
              </span>
              <p className="text-xs text-rose-900 leading-relaxed">
                Revoking the key for <span className="font-extrabold">{apiKey.platform}</span> (
                <code className="font-mono text-xs font-bold">{apiKey.keyHint}</code>) cannot be
                undone. Any production systems using this key will immediately fail with{' '}
                <code className="font-mono text-xs font-semibold">401 Unauthorized</code>.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="revoke-confirm" className="text-xs font-bold text-foreground-800">
              Type <span className="font-extrabold text-foreground-950">&quot;{apiKey.platform}&quot;</span> to
              confirm
            </label>
            <input
              id="revoke-confirm"
              type="text"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              placeholder={apiKey.platform}
              className={inputClasses}
            />
          </div>

          {error && (
            <p className="text-xs font-semibold text-rose-700 bg-rose-100/70 border border-rose-200 rounded-xl px-3.5 py-2 animate-shake">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-background-200/80">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap px-4 py-2 rounded-xl bg-background-100 hover:bg-background-200 border border-background-200 text-xs font-bold text-foreground-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="whitespace-nowrap px-4.5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow-md shadow-rose-600/20 transition-all cursor-pointer"
            >
              Revoke Key Permanently
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
